import { useEffect, useRef, useState } from 'react';
import { Check, Copy, Snowflake as SnowflakeGlyph } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip';
import { formatCreatedAt, parseSnowflake, shownBody } from '@/lib/snowflake';
import { cn } from '@/lib/utils';

/**
 * A snowflake id, shown the same way everywhere: a hairline chip with a ❄
 * glyph, the prefix muted and the hex in ink. Clicking copies the FULL id,
 * truncated or not; the tooltip names the record type, shows the whole id and
 * the creation time decoded from it.
 *
 * - `size`: "md" (meta lines, panels, card footers) or "sm" (table rows,
 *   inline in text).
 * - `truncate`: keep the prefix and the time-ordered head of the hex.
 * - An id that is not a snowflake (a request UUID, a legacy key) renders the
 *   same chip without the ❄ and is labelled plainly "ID".
 * - `copyable={false}`: the same chip without the button, for an id inside
 *   something that is already clickable (a search result, a picker option),
 *   where a button inside a button would be invalid and unreachable.
 *
 * Inside a link or a clickable row the copy click neither navigates nor
 * reaches the row. It does not toast: a table of ids would spam
 * notifications; the check glyph, the tooltip and a live region say it.
 */
export default function Snowflake({ id, size = 'md', truncate = false, copyable = true, className }) {
    const parsed = parseSnowflake(id);
    const [copied, setCopied] = useState(false);
    const [open, setOpen] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);

    if (!parsed.id) {
        return null;
    }

    const sm = size === 'sm';
    const created = formatCreatedAt(parsed.createdAt);
    const chip = cn(
        'group inline-flex max-w-full min-w-0 items-center gap-1 whitespace-nowrap rounded-md bg-zinc-50 align-middle font-mono text-zinc-700 ring-1 ring-inset ring-zinc-200',
        sm ? 'h-5 px-1 text-[11px]' : 'h-6 px-1.5 text-[12px]',
        className,
    );
    // The ❄ says "this is a snowflake", so an id that is not one — a request
    // UUID, a legacy key — keeps the chip and the copy but not the glyph.
    const glyph = parsed.prefix
        ? <SnowflakeGlyph aria-hidden="true" className={cn('shrink-0 text-zinc-400', sm ? 'size-2.5' : 'size-3')} />
        : null;
    const text = (
        <span className="min-w-0 truncate select-all">
            {parsed.prefix && <span className="text-zinc-400">{parsed.prefix}_</span>}
            {shownBody(parsed, truncate)}
        </span>
    );

    if (!copyable) {
        return (
            <span className={chip} title={parsed.id} data-snowflake={parsed.id}>
                {glyph}
                {text}
            </span>
        );
    }

    const copy = (event) => {
        // A chip inside a link or a clickable row copies; it does not open.
        event.preventDefault();
        event.stopPropagation();
        navigator.clipboard?.writeText(parsed.id).catch(() => {});
        setCopied(true);
        setOpen(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 1200);
    };

    return (
        <TooltipProvider delayDuration={250}>
            <Tooltip open={open} onOpenChange={setOpen}>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        onClick={copy}
                        // Radix closes a tooltip on pointer-down, which would
                        // hide it just before "Copied" is meant to appear; a
                        // prevented event is one its handlers leave alone.
                        onPointerDown={(event) => event.preventDefault()}
                        // Keep a row's own keyboard handling (Enter to open)
                        // from firing when the chip is the one focused.
                        onKeyDown={(event) => event.stopPropagation()}
                        aria-label={`Copy ${parsed.type.toLowerCase()} ${parsed.prefix ? 'snowflake ' : ''}${parsed.id}`}
                        data-snowflake={parsed.id}
                        className={cn(
                            chip,
                            'relative cursor-copy transition-colors hover:bg-white hover:ring-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20',
                        )}
                    >
                        {glyph}
                        {text}
                        <span aria-hidden="true" className={cn('shrink-0 text-zinc-400', sm ? 'size-2.5' : 'size-3')}>
                            {copied ? (
                                <Check className="size-full text-emerald-600" />
                            ) : (
                                <Copy className="size-full opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
                            )}
                        </span>
                        <span className="sr-only" aria-live="polite">{copied ? 'Copied' : ''}</span>
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[320px] rounded-lg bg-zinc-900 px-3 py-2.5 text-white shadow-lg">
                    <span className="flex items-center gap-1.5 text-[11.5px] text-zinc-400">
                        <SnowflakeGlyph aria-hidden="true" className="size-3" />
                        {parsed.prefix ? `${parsed.type} snowflake` : parsed.type}
                    </span>
                    <span className="mt-1 block break-all font-mono text-[12.5px]">{parsed.id}</span>
                    {created && <span className="mt-1.5 block text-[11.5px] tabular-nums text-zinc-400">Created {created}</span>}
                    <span className={cn('mt-2 flex items-center gap-1.5 border-t border-white/10 pt-2 text-[11.5px]', copied ? 'text-emerald-400' : 'text-zinc-400')}>
                        {copied ? <><Check aria-hidden="true" className="size-3" />Copied</> : 'Click to copy'}
                    </span>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
